import React, { useEffect, useMemo, useState } from 'react';

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const Lightbox = ({ open, items, initialIndex = 0, onClose }) => {
  const safeItems = useMemo(() => Array.isArray(items) ? items.filter(Boolean) : [], [items]);
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (!open) return;
    setIndex(clamp(initialIndex, 0, Math.max(0, safeItems.length - 1)));
  }, [open, initialIndex, safeItems.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + safeItems.length) % safeItems.length);
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % safeItems.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, safeItems.length]);

  if (!open || safeItems.length === 0) return null;

  const current = safeItems[index] || safeItems[0];

  const prev = () => setIndex((i) => (i - 1 + safeItems.length) % safeItems.length);
  const next = () => setIndex((i) => (i + 1) % safeItems.length);

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 md:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
    >
      <div className="relative w-full max-w-[95vw] md:max-w-[1100px]" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-3 -right-3 z-10 w-11 h-11 rounded-full bg-white/10 border border-white/20 backdrop-blur text-white flex items-center justify-center hover:bg-white/15 transition-all shadow-lg"
          aria-label="Close"
        >
          <i className="fas fa-xmark text-lg"></i>
        </button>

        {safeItems.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 border border-white/20 backdrop-blur text-white flex items-center justify-center hover:bg-white/15 transition-all shadow-lg z-10"
              aria-label="Previous"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button
              type="button"
              onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 border border-white/20 backdrop-blur text-white flex items-center justify-center hover:bg-white/15 transition-all shadow-lg z-10"
              aria-label="Next"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </>
        )}

        <div className="glass-card overflow-hidden border border-white/15">
          <div className="bg-black/20">
            <img
              src={current.img || current.src}
              alt={current.alt || current.title || 'Preview'}
              className="w-auto h-auto max-h-[80vh] mx-auto object-contain"
              onError={(e) => {
                if (!e.currentTarget.dataset.fallbackTried) {
                  e.currentTarget.dataset.fallbackTried = '1';
                  const cur = e.currentTarget.src;
                  if (cur.includes('/admin/uploads/')) {
                    e.currentTarget.src = cur.replace('/admin/uploads/', '/admin/public/uploads/');
                  } else if (cur.includes('/admin/public/uploads/')) {
                    e.currentTarget.src = cur.replace('/admin/public/uploads/', '/admin/uploads/');
                  }
                }
              }}
            />
          </div>
          <div className="px-4 py-3 text-center">
            <div className="text-primary text-sm font-semibold">
              {current.alt || current.title || 'Preview'}
            </div>
            {safeItems.length > 1 && (
              <div className="text-muted text-xs mt-1">
                {index + 1} / {safeItems.length}
              </div>
            )}
          </div>
        </div>

        {safeItems.length > 1 && (
          <div className="mt-4 hidden md:block">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {safeItems.slice(0, 18).map((it, i) => (
                <button
                  key={`${it.img || it.src}-${i}`}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={`relative w-[86px] h-[64px] rounded-lg overflow-hidden border transition-all ${i === index ? 'border-secondary shadow-md' : 'border-white/15 opacity-80 hover:opacity-100'}`}
                  aria-label={`Preview ${i + 1}`}
                >
                  <img src={it.img || it.src} alt={it.alt || it.title || `Preview ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  {i === index && <div className="absolute inset-0 ring-2 ring-secondary/40"></div>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Lightbox;

